import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import DialogMUI from '../../../shared/DialogMUI';
import {closeModal} from '../../../redux/actions/app/appActions';
import ButtonMUI from '../../../shared/ButtonMUI';
import {POPUPS} from '../../../redux/actions/app/appActionTypes';
import {useHistory} from 'react-router-dom';
import {mainPath} from '../../../routes/paths';
import './MailingPopup.scss';
import {Controller, useForm} from 'react-hook-form';
import SelectComponent from '../../../shared/SelectComponent';
import InputMUI from '../../../shared/InputMUI';
import Dropzone from '../../Components/Dropzone';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {AutocompleteMUI, DatePickerMUI} from '../../../shared';
import TimePickerMUI from '../../../shared/TimePickerMUI';
import {
  addMailing,
  clearEditMailingData,
  editMailing,
  getMailings
} from '../../../redux/actions/mailing/mailingActions';
import {useTranslation} from 'react-i18next';
import {getProjectTypes} from '../../../redux/actions/projects/projectsActions';
import {getCities, getCountries} from '../../../redux/actions/countries/countriesActions';
import {notifyError, notifySuccess} from '../../../helpers/notifySnack';
import {getUserRoles} from '../../../redux/actions/profile/profileActions';
import LoadPanel from '../../Components/LoadPanel';
import dayjs from 'dayjs';

const MailingPopup = () => {
  const {countriesLoading, countries, cities, citiesLoading} = useSelector(({countries}) => countries);
  const companyList = useSelector(({company}) => company.companies);
  const companyLoading = useSelector(({company}) => company.companiesLoading);
  const {projectTypes, projectTypesLoading} = useSelector(({projects}) => projects);
  const [filteredCities, setFilteredCities] = useState([]);
  const {userRoles} = useSelector((state) => state.profile);

  const dispatch = useDispatch();
  const popupToShow = useSelector(({app}) => app.popupToShow);
  const editMailingData = useSelector(({mailing}) => mailing.editData);
  const isView = useSelector(({mailing}) => mailing.isView);
  const {t} = useTranslation();

  const hasEditMailingData = Object.keys(editMailingData).length > 0;

  const onCloseHandler = () => {
    dispatch(closeModal());
    setTimeout(() => {
      reset({
        companyId: '',
        userType: [],
        countryId: '',
        cityId: '',
        projectTypeId: [],
        date: null,
        time: null,
        attachment: [],
        text: ''
      });
      dispatch(clearEditMailingData(false));
    }, 150);
  };

  function combineDateTime(date, time) {
    const parsedDate = dayjs(date);
    const parsedTime = dayjs(time);

    return parsedDate.hour(parsedTime.hour()).minute(parsedTime.minute()).second(0);
  }

  const onSubmit = async (data) => {
    const date = data.date;
    const time = data.time;

    const parsedDate = combineDateTime(date, time).format();

    const formData = new FormData();

    if(data.cityId){
      formData.append('cityId', data.cityId);
    }
    formData.append('companyId', data.companyId);
    formData.append('countryId', data.countryId);
    formData.append('text', data.text);
    formData.append('sentAt', parsedDate);
    if (data.projectTypeId && data.projectTypeId.length) {
      data.projectTypeId.forEach((el, idx) => {
        formData.append(`projectTypeId[${idx}]`, el.id);
      });
    }
    if (data.userType && data.userType.length) {
      data.userType.forEach((el, idx) => {
        formData.append(`userType[${idx}]`, el.id);
      });
    }

    // formData.append('userType', data.userType);
    if (data.attachment && data.attachment.length > 0) {
      if(!data.attachment[0]?.preview){ // якщо файл був уже  прикріплений
        formData.append('file', data.attachment[0]);
      }
    }
    else {
      formData.append('file', '');
    }
    if (!hasEditMailingData) {
      const res = await dispatch(addMailing(formData));
      if (res.payload) {
        onCloseHandler();
        notifySuccess(t('all.successfully'));
        await dispatch(getMailings());
      }
    } else {
      const res = await dispatch(editMailing(formData, editMailingData.id));
      if (res.payload) {
        onCloseHandler();
        notifySuccess(t('all.successfully'));
        await dispatch(getMailings());
      }
    }
  };

  const onFilesAdded = (files) => {
    setValue('attachment', files);
  };

  const schema = yup.object().shape({
    companyId: yup.string().required(t('MailingPopup.errors.companyRequired')),
    countryId: yup.string().required(t('MailingPopup.errors.countryRequired')),
    text: yup.string().required(t('MailingPopup.errors.descriptionRequired')),
    date: yup.date().required(t('MailingPopup.errors.dateRequired')),
    time: yup.date().required(t('MailingPopup.errors.timeRequired'))
  });

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: {errors, isSubmitting, isValid}
  } = useForm({
    mode: 'all',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    resolver: yupResolver(schema),
    defaultValues: {
      companyId: '',
      userType: [],
      countryId: '',
      cityId: '',
      projectTypeId: [],
      date: null,
      time: null,
      attachment: [],
      text: ''
    }
  });
  const popupVisible = popupToShow === POPUPS.MAILING_POPUP;

  const handleCountryChange = (selectedCountryId) => {
    const filtered = cities.filter((city) => city.countryId === selectedCountryId);
    setFilteredCities(filtered);
  };

  const filteredRoles = userRoles.filter((el) => el.role !== 'demo');

  const mapppedRoles = filteredRoles.map((el) => {
    return {
      id: el.role,
      label: el.name
    };
  });

  useEffect(() => {
    const countryId = watch('countryId');
    handleCountryChange(countryId);
  }, [cities, watch('countryId')]);

  useEffect(() => {
    const fetchFunc = async () => {
      try {
        await dispatch(getUserRoles());

        if (projectTypesLoading) {
          const projectTypesRes = await dispatch(getProjectTypes());
          if (projectTypesRes.error) {
            onCloseHandler();
            return;
          }
        }

        if (countriesLoading) {
          const countriesRes = await dispatch(getCountries());
          if (countriesRes.error) {
            onCloseHandler();
            return;
          }
        }

        if (citiesLoading) {
          const citiesRes = await dispatch(getCities());
          if (citiesRes.error) {
            onCloseHandler();
          }
        }
      } catch (error) {
        notifyError(error.message || 'error');
      }
    };

    if (popupVisible) {
      fetchFunc();
    } else {
      reset({
        companyId: '',
        userType: [],
        countryId: '',
        cityId: '',
        projectTypeId: [],
        date: null,
        time: null,
        attachment: [],
        text: ''
      });
    }
  }, [popupVisible, dispatch]);

  useEffect(() => {
    if (hasEditMailingData) {
      const projectsTypesMap = editMailingData?.projectTypeId?.map((el) => {
        return {
          id: el.id,
          label: el.name
        };
      });

      const userRolesMap = editMailingData?.userType?.map((el) => ({id: el.type, label: el.name}));

      reset({
        companyId: editMailingData.companyId ?? [],
        userType: userRolesMap ?? [],
        countryId: editMailingData.countryId,
        cityId: editMailingData.cityId,
        projectTypeId: projectsTypesMap ?? [],
        date: editMailingData.sentAt,
        time: editMailingData.sentAt,
        attachment: [],
        text: editMailingData.text
      });
    }
  }, [hasEditMailingData]);

  const formatTitle = isView ? t('MailingPopup.viewMailing') : t('MailingPopup.editMailing');

  const projectTypesAll = projectTypes?.map((el) => {
    return {
      id: el.id,
      label: el.name ?? ''
    };
  });
  return (
    <div className='mailing_popup'>
      <DialogMUI
        maxWidth={'sm'}
        open={popupVisible}
        onClose={onCloseHandler}
        title={hasEditMailingData ? formatTitle : t('MailingPopup.createMailing')}
      >
        {countriesLoading || citiesLoading || projectTypesLoading || companyLoading ? (
          <LoadPanel staticPos={true} noBg={true} />
        ) : (
          <>
            <div className='mailing-popup__select'>
              <Controller
                name='companyId'
                control={control}
                render={({field}) => (
                  <SelectComponent
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    value={companyList.find((c) => c.id === field.value)}
                    options={companyList}
                    error={errors.companyId?.message}
                    label={t('MailingPopup.selectCompany')}
                    placeholder=''
                    inputProps={field}
                    disabled={isView}
                  />
                )}
              />
            </div>
            <div className='mailing-popup__select'>
              <Controller
                name='userType'
                control={control}
                render={({field}) => (
                  <AutocompleteMUI
                    className='no-icons'
                    text={t('MailingPopup.selectUser')}
                    inputProps={field}
                    array={mapppedRoles}
                    disabled={isView}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
            <div className='mailing-popup__input'>
              <Controller
                name='countryId'
                control={control}
                render={({field}) => (
                  <SelectComponent
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    value={countries.find((c) => c.id === field.value)}
                    options={countries}
                    error={errors.countryId?.message}
                    label={t('ProjectsModal.country')}
                    placeholder=''
                    inputProps={field}
                    disabled={isView}
                  />
                )}
              />
            </div>
            <div className='mailing-popup__input'>
              <Controller
                name='cityId'
                control={control}
                render={({field}) => (
                  <SelectComponent
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    value={filteredCities.find((c) => c.id === field.value)}
                    options={filteredCities}
                    label={t('ProjectsModal.city')}
                    error={errors.cityId?.message}
                    placeholder=''
                    inputProps={field}
                    disabled={!filteredCities.length || isView}
                  />
                )}
              />
            </div>
            <div className='mailing-popup__select'>
              <Controller
                name='projectTypeId'
                control={control}
                render={({field}) => (
                  // <SelectComponent
                  //   onChange={field.onChange}
                  //   onBlur={field.onBlur}
                  //   value={projectTypesAll.find((c) => c.id === field.value)}
                  //   options={projectTypesAll}
                  //   label={t('MailingPopup.projectType')}
                  //   error={errors.projectTypeId?.message}
                  //   placeholder=''
                  //   inputProps={field}
                  //   disabled={isView}
                  // />
                  <AutocompleteMUI
                    className='no-icons'
                    text={t('MailingPopup.projectType')}
                    inputProps={field}
                    array={projectTypesAll}
                    disabled={isView}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>

            <div className='mailing-popup__pickers'>
              <div className='mailing-popup__picker'>
                <Controller
                  name='date'
                  control={control}
                  render={({field: {onChange, value}}) => {
                    return <DatePickerMUI value={value} onChange={(date) => onChange(date)} disabled={isView} />;
                  }}
                />
              </div>
              <div className='mailing-popup__picker'>
                <Controller
                  name='time'
                  control={control}
                  render={({field: {onChange, value}}) => (
                    <TimePickerMUI value={value} onChange={(date) => onChange(date)} disabled={isView} />
                  )}
                />
              </div>
            </div>
            <div className='mailing-popup__textarea'>
              <div className='mailing-popup__name'>{t('MailingPopup.messageText')}</div>
              <Controller
                name='text'
                control={control}
                render={({field}) => (
                  <InputMUI
                    className='no-icons'
                    type='text'
                    fullWidth
                    error={errors.text?.message}
                    inputProps={field}
                    multiline={true}
                    placeholder={t('MailingPopup.placeholderMessage')}
                    minRows={3.3}
                    maxRows={4}
                    disabled={isView}
                  />
                )}
              />
            </div>
            <div className='mailing-popup__dropzone'>
              <Dropzone
                maxFiles={1}
                disabled={isView}
                maxSizeMb={50}
                onFilesAdded={onFilesAdded}
                initialImageUrl={editMailingData?.file}
              />
            </div>

            <ButtonMUI
              disabled={!isValid || isSubmitting || isView}
              loading={isSubmitting}
              onClick={handleSubmit(onSubmit)}
              fullWidth={true}
            >
              {hasEditMailingData ? t('MailingPopup.saveChanges') : t('MailingPopup.createMailing')}
            </ButtonMUI>
          </>
        )}
      </DialogMUI>
    </div>
  );
};

export default MailingPopup;
